import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FileText, Users, FolderTree, Image, Flag, Settings, Palette, Activity, Trophy, Clock, Shuffle, History, Unlink, HelpCircle, PanelLeft } from "lucide-react";

const CARDS = [
  { key: "articles", label: "Articles", icon: FileText, to: "/admin/articles", desc: "Create, edit, and delete articles" },
  { key: "quizzes", label: "Quizzes", icon: HelpCircle, to: "/admin/quizzes", desc: "Build and customise quizzes" },
  { key: "navigation", label: "Sidebar Navigation", icon: PanelLeft, to: "/admin/navigation", desc: "Customise or hide each sidebar" },
  { key: "drafts", label: "Draft Submissions", icon: Clock, to: "/admin/drafts", desc: "Review editor submissions" },
  { key: "redirects", label: "Redirects", icon: Shuffle, to: "/admin/redirects", desc: "Point old names to the right entry" },
  { key: "broken-links", label: "Broken Links", icon: Unlink, to: "/admin/broken-links", desc: "Find links to missing articles" },
  { key: "content-history", label: "Edit History", icon: History, to: "/admin/content-history", desc: "Undo accidental content edits" },
  { key: "users", label: "Users", icon: Users, to: "/admin/users", desc: "Registered accounts" },
  { key: "categories", label: "Categories", icon: FolderTree, to: "/admin/categories", desc: "Manage categories" },
  { key: "images", label: "Images", icon: Image, to: "/admin/images", desc: "Uploaded media" },
  { key: "reports", label: "Reports", icon: Flag, to: "/admin/reports", desc: "Suggested edits & contributions" },
  { key: "achievements", label: "Achievements", icon: Trophy, to: "/admin/achievements", desc: "Manage achievements" },
  { key: "settings", label: "Settings", icon: Settings, to: "/admin/settings", desc: "Site title & footer" },
  { key: "appearance", label: "Site Appearance", icon: Palette, to: "/admin/settings", desc: "Theme & branding" },
  { key: "activity", label: "Activity Log", icon: Activity, to: "/admin/activity", desc: "Recent admin actions" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const statFor = (key) => {
    const map = { articles: stats.articles, drafts: stats.drafts, users: stats.users, categories: stats.categories, images: stats.images, reports: stats.pending, achievements: stats.achievements };
    return map[key];
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <h1 className="font-serif-arc text-3xl font-black tracking-tight sm:text-4xl">Archive Administration</h1>
      <p className="mt-2 text-sm" style={{ color: "var(--arc-text-2)" }}>Manage the contents of The Archives.</p>
      <div className="mt-4 rounded-md border-l-4 p-4 text-sm" style={{ borderLeftColor: "var(--ghoulax)", backgroundColor: "var(--ghoulax-soft)" }} data-testid="inline-edit-hint">
        <span className="font-semibold">Tip:</span> While signed in as admin, you can edit almost any text or image directly on the site — just hover and click it. Changes save instantly for everyone.
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total Articles" value={stats.articles} />
        <Stat label="Pending Drafts" value={stats.drafts} />
        <Stat label="Pending Reports" value={stats.pending} />
        <Stat label="Users" value={stats.users} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((c) => (
          <Link key={c.key} to={c.to} data-testid={`admin-card-${c.key}`} className="group rounded-md border p-6 shadow-sm transition-shadow hover:shadow-md" style={{ backgroundColor: "var(--arc-card)" }}>
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-md" style={{ backgroundColor: "var(--ghoulax-soft)" }}>
                <c.icon size={20} style={{ color: "var(--ghoulax)" }} />
              </div>
              {statFor(c.key) !== undefined && <span className="font-serif-arc text-2xl font-black" style={{ color: "var(--arc-text-2)" }}>{statFor(c.key)}</span>}
            </div>
            <h2 className="mt-4 font-serif-arc text-lg font-bold group-hover:underline">{c.label}</h2>
            <p className="mt-1 text-sm" style={{ color: "var(--arc-text-2)" }}>{c.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border p-4 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
      <p className="text-xs uppercase tracking-wider" style={{ color: "var(--arc-text-2)" }}>{label}</p>
      <p className="mt-1 font-serif-arc text-2xl font-black">{value ?? "—"}</p>
    </div>
  );
}
